import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  INJECTOR,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';
import { Kasse, WorkstationConfigService } from '@softapps/wws/pos-core';
import {
  backNavigation,
  handleRequestError,
  menuItems,
  title,
} from '@softline/application';
import {
  JwtAuthenticationStore,
  SOFTLINE_FEATURE_JWT_AUTHENTICATION,
  UserInfo,
  UserInfoService,
} from '@softline/auth';
import { isDefinedNotEmpty, Store } from '@softline/core';
import { FieldOkComponent } from '@softline/dynamic';
import { MessageBarService, TranslatePipe, translator } from '@softline/ui-core';
import { SOFTLINE_CONST_WWS_POS_SELF_CHECKOUT_CONFIG_KEY } from '../../self-checkout.shared';
import { SOFTLINE_CONFIG_WWS_SELF_CHECKOUT_PARAMS } from '../../self-checkout.tokens';
import { Configuration } from '../../types/configuration';

@Component({
  selector: 'soft-config',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FieldOkComponent, PageComponent, PageHeaderComponent, TranslatePipe],
  templateUrl: './config.component.html',
  styleUrl: './config.component.css',
})
export default class ConfigComponent implements AfterViewInit {
  readonly #store = inject(Store);
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #injector = inject(INJECTOR);
  readonly messageBarService = inject(MessageBarService);
  readonly #workstationDisplayService = inject(WorkstationConfigService);
  readonly #userInfo = inject<UserInfoService<UserInfo & { firmaid?: number }>>(UserInfoService);
  readonly #params = inject(SOFTLINE_CONFIG_WWS_SELF_CHECKOUT_PARAMS, { optional: true });
  private readonly t = translator();

  readonly workstation = signal<Kasse | undefined>(undefined);

  readonly isValueValid = computed(() => {
    return isDefinedNotEmpty(this.workstation());
  });

  // the token's firmaid wins; the module config only fills in when the claim is absent
  readonly companyId = computed(
    () => this.#userInfo.value()?.firmaid ?? this.#params?.companyId
  );

  constructor() {
    backNavigation('/');
    title(this.t('#SELF_CHECKOUT_CONFIG.WORKSTATION.TITLE'));
    menuItems(() => {
      return [
        {
          name: 'self-checkout/config/workstation/submit',
          title: '#SELF_CHECKOUT_CONFIG.WORKSTATION.SUBMIT',
          outlet: 'responsive',
          class: 'soft-button primary',
          type: 'execute',
          canExecute: this.isValueValid(),
          execute: () => this.onSubmit(),
        },
        {
          name: 'self-checkout/config/workstation',
          title: '#SELF_CHECKOUT_CONFIG.WORKSTATION.TITLE',
          icon: 'fa-regular fa-gear',
          type: 'route',
          outlet: 'nav',
          routerLink: ['/self-checkout/config'],
        },
      ];
    });
  }

  async ngAfterViewInit(): Promise<void> {
    const config = await this.#workstationDisplayService.load(
      SOFTLINE_CONST_WWS_POS_SELF_CHECKOUT_CONFIG_KEY
    );

    this.workstation.set((config as Configuration).name as Kasse | undefined);
  }

  async saveConfiguration() {
    const value = this.workstation();

    if (!value) return;

    const config: Configuration = {
      name: value,
    };

    await this.#workstationDisplayService.save<Configuration>(
      SOFTLINE_CONST_WWS_POS_SELF_CHECKOUT_CONFIG_KEY,
      config
    );
  }

  async onSubmit() {
    try {
      await this.saveConfiguration();
      await this.#store.dispatch(
        SOFTLINE_FEATURE_JWT_AUTHENTICATION,
        JwtAuthenticationStore.actions.change,
        { workstation: this.workstation()?.kassennummer, firmaid: this.companyId() }
      );

      this.messageBarService.success('#SELF_CHECKOUT_CONFIG.WORKSTATION.SUCCESS_MESSAGE');
      await this.#router.navigate(['./../../'], {
        relativeTo: this.#activatedRoute,
      });
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }
}
